import { useQuery } from '@tanstack/react-query'
import { contentfulApi } from '../services/contentfulService'
import { useMemo } from 'react'

/**
 * Hook refactorizado para obtener datos de Your Condition
 * @returns {Object} Datos procesados de Your Condition
 */
export const useContentfulYourCondition = () => {
	const {
		data,
		isLoading: loading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'yourCondition'],
		queryFn: contentfulApi.getYourCondition,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Procesar datos usando useMemo para evitar recálculos innecesarios
	const processedData = useMemo(() => {
		if (!data?.homeCollection?.items?.[0]?.vistas?.tuCondicion?.beneficioCollection?.items) {
			return {
				yourConditionData: false,
				medicineData: false,
				IMCData: false,
				medicControl: false,
				thermoAndTensiometer: false,
				symptomsData: false,
				dailyTitle: false
			}
		}

		const items = data.homeCollection.items[0].vistas.tuCondicion.beneficioCollection.items

		// Procesar yourConditionData
		let base: Record<string, any> = {}
		let dailyTitle = false

		items?.forEach((e: any) => {
			base = { ...base, [e.idReto]: e?.beneficioCollection?.items }
			if (e?.idReto === 'infoDiaria') {
				dailyTitle = e?.tituloGeneral
			}
		})

		// Procesar medicineData
		const medicineData = items?.filter((e: any) => e.idReto === 'medicina')[0]

		// Procesar IMCData
		const IMCData = items?.filter((e: any) => e.idReto === 'medicion')[0]

		// Procesar medicControl
		const newMedicControl = items?.filter((e: any) => e.idReto === 'controlMedico')[0]
		let listMedicControl: Record<string, any> = {}

		newMedicControl?.beneficioCollection?.items?.forEach((e: any) => {
			listMedicControl = {
				...listMedicControl,
				[e.idReto]: e
			}
		})

		const medicControl = {
			tituloGeneral: newMedicControl?.tituloGeneral,
			butonactualizar: newMedicControl?.textoBoton,
			listadatos: listMedicControl
		}

		// Procesar thermoAndTensiometer
		const newThermoControl = items?.filter((e: any) => e?.idReto === 'termometroTensiometro')?.[0]
			?.beneficioCollection?.items

		let newThermo: Record<string, any> = {}
		newThermoControl?.forEach((e: any) => {
			newThermo = {
				...newThermo,
				[e.idReto]: {
					img: {
						src: e?.imagen?.url,
						alt: e?.tituloGeneral
					}
				}
			}
		})

		const thermoAndTensiometer = {
			lista: newThermoControl?.map((e: any) => e.idReto),
			imagen: newThermo
		}

		// Procesar symptomsData
		const symptomsData = items?.filter((e: any) => e.idReto === 'seccionSintomas')?.[0]

		return {
			yourConditionData: base,
			medicineData,
			IMCData,
			medicControl,
			thermoAndTensiometer,
			symptomsData,
			dailyTitle
		}
	}, [data])

	// Información del cache
	const cacheInfo = useMemo(() => {
		const now = new Date()
		const cacheExpiry = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000)
		return {
			cacheExpiry,
			lastUpdated: now
		}
	}, [])

	// Logging para debugging
	if (error) {
		console.error('Error en useContentfulYourCondition:', error)
	}

	return {
		loading,
		error,
		...processedData,
		refetch,
		isStale,
		isRefetching,
		cacheInfo,
		rawData: data
	}
}

export default useContentfulYourCondition
